import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { Button, DatePicker, Select, Tag } from 'antd'
import moment from 'moment'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { ActionEnum, getDateFormat, getPumpShiftStatus } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  NumberFormat,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { PumpShiftEntity, PumpShiftStatusEnum } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadPumpShifts,
  loadShifts,
  setAction,
  setPumpShiftFilter,
  setShowManageModal,
  setShowSearchDrawer,
} from '../../../stores/actions'
import {
  ManagePumpShiftModalComponent,
  SearchPumpShiftDrawerComponent,
} from './components'

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`

const FooterElement = styled.div`
  margin-right: 20px;
  font-weight: bold;
`

export const PumpShiftPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { shifts } = useSelector((state: RootState) => state.shift)
  const {
    pumpShiftFilter,
    pumpShifts,
    isPumpShiftsLoaded,
    totalPumpShifts,
    totalAmountOfPumpsShift,
    totalVolumeOfPumpsShift,
  } = useSelector((state: RootState) => state.pumpShift)

  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  const [currentTime, setCurrentTime] = React.useState(new Date().getTime())
  const [filter, setFilter] = React.useState(pumpShiftFilter)
  const [selectedPumpShiftId, setSelectedPumpShiftId] = React.useState(-1)

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
  }, [dispatch, isDesktopScreen])

  React.useEffect(() => {
    setFilter(pumpShiftFilter)
    dispatch(loadPumpShifts())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pumpShiftFilter])

  React.useEffect(() => {
    dispatch(loadPumpShifts())
    dispatch(loadShifts())
    // eslint-disable-next-line
  }, [])

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentTime(currentTime + 5000)
    }, 5000)
    return () => clearTimeout(timer)
  }, [currentTime])

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('pumpShift.id', language),
        width: 50,
      },
      {
        title: (
          <>
            <span>{translate('pumpShift.createdDate', language)}</span>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={[
                translate('common.from', language),
                translate('common.to', language),
              ]}
              value={[
                moment(filter.createdDateFrom),
                moment(filter.createdDateTo),
              ]}
              format="DD/MM/YYYY"
              onChange={(e) => {
                setFilter({
                  ...filter,
                  createdDateFrom:
                    e && e.length === 2 && e[0]
                      ? e[0].toDate().getTime()
                      : filter.createdDateFrom,
                  createdDateTo:
                    e && e.length === 2 && e[1]
                      ? e[1].toDate().getTime()
                      : filter.createdDateTo,
                })
              }}
            />
          </>
        ),
        width: 130,
        render: (_: any, pumpShift: PumpShiftEntity) =>
          getDateFormat(pumpShift.createdDate),
      },
      {
        dataIndex: 'closedTime',
        title: (
          <>
            <span>{translate('pumpShift.closedTime', language)}</span>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={[
                translate('common.from', language),
                translate('common.to', language),
              ]}
              value={
                filter.closedTimeFrom && filter.closedTimeTo
                  ? [moment(filter.closedTimeFrom), moment(filter.closedTimeTo)]
                  : undefined
              }
              format="DD/MM/YYYY"
              onChange={(e) => {
                setFilter({
                  ...filter,
                  closedTimeFrom:
                    e && e.length === 2 && e[0]
                      ? e[0].toDate().getTime()
                      : filter.closedTimeFrom,
                  closedTimeTo:
                    e && e.length === 2 && e[1]
                      ? e[1].toDate().getTime()
                      : filter.closedTimeTo,
                })
              }}
            />
          </>
        ),
        width: 130,
        render: (_: any, pumpShift: PumpShiftEntity) =>
          pumpShift.closedTime ? getDateFormat(pumpShift.closedTime, true) : '',
      },
      {
        title: (
          <>
            <span>{translate('common.status', language)}</span>
            {isDesktopScreen && (
              <Select
                mode="multiple"
                maxTagCount="responsive"
                style={{ width: '100%' }}
                value={filter.statuses}
                onChange={(e) => {
                  setFilter({
                    ...filter,
                    statuses: e,
                  })
                }}
              >
                {Object.values(PumpShiftStatusEnum).map((item) => (
                  <Select.Option value={item}>
                    {translate(
                      `pumpShift.status.${item.toLowerCase()}`,
                      language
                    )}
                  </Select.Option>
                ))}
              </Select>
            )}
          </>
        ),
        width: 150,
        render: (_: any, pumpShift: PumpShiftEntity) => {
          const status = getPumpShiftStatus(pumpShift, shifts, currentTime)
          return (
            <Tag
              color={
                status === PumpShiftStatusEnum.CLOSED
                  ? 'success'
                  : status === PumpShiftStatusEnum.WORKING
                  ? 'processing'
                  : status === PumpShiftStatusEnum.UNCLOSE
                  ? 'warning'
                  : 'default'
              }
              icon={
                status === PumpShiftStatusEnum.CLOSED ? (
                  <CheckCircleOutlined />
                ) : status === PumpShiftStatusEnum.WORKING ? (
                  <SyncOutlined spin />
                ) : status === PumpShiftStatusEnum.UNCLOSE ? (
                  <ExclamationCircleOutlined />
                ) : (
                  <ClockCircleOutlined />
                )
              }
            >
              {translate(`pumpShift.status.${status.toLowerCase()}`, language)}
            </Tag>
          )
        },
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="pumpShift.station"
            value={filter.stationName}
            onChange={(e) =>
              setFilter({
                ...filter,
                stationName: e.target.value,
              })
            }
          />
        ),
        width: 150,
        render: (_: any, pumpShift: PumpShiftEntity) =>
          pumpShift.pump.tank.station.name,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="pumpShift.shift"
            value={filter.shiftName}
            onChange={(e) =>
              setFilter({
                ...filter,
                shiftName: e.target.value,
              })
            }
          />
        ),
        width: 150,
        render: (_: any, pumpShift: PumpShiftEntity) => pumpShift.shift.name,
      },
      {
        title: (
          <ColumnInputHeader
            titleKey="pumpShift.pump"
            value={filter.pumpName}
            onChange={(e) =>
              setFilter({
                ...filter,
                pumpName: e.target.value,
              })
            }
          />
        ),
        width: 150,
        render: (_: any, pumpShift: PumpShiftEntity) =>
          `${pumpShift.pump.name} (${pumpShift.pump.tank.fuel.name})`,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => {
              dispatch(
                setPumpShiftFilter({
                  ...filter,
                  pageIndex: 1,
                })
              )
            }}
          />
        ),
        render: (_: any, pumpShift: PumpShiftEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              setSelectedPumpShiftId(pumpShift.id)
              dispatch(setAction(ActionEnum.VIEW))
            }}
          />
        ),
        width: 60,
      },
    ],
    [currentTime, dispatch, filter, isDesktopScreen, language, shifts]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && (
        <ManagePumpShiftModalComponent pumpShiftId={selectedPumpShiftId} />
      )}
      <PageTitleComponent
        hasSearchDrawer
        customButtons={
          <Button
            onClick={() => {
              setSelectedPumpShiftId(-1)
              dispatch(setShowManageModal(true))
            }}
            type="primary"
            style={{ margin: '20px 0', float: 'right' }}
            icon={<HomeOutlined />}
            size={!isDesktopScreen ? 'small' : 'middle'}
          >
            {translate('pumpShift.closePumpShiftByStation', language)}
          </Button>
        }
      />
      {management.showSearchDrawer && <SearchPumpShiftDrawerComponent />}
      <TableComponent
        paginationConfig={{
          current: pumpShiftFilter.pageIndex,
          total: totalPumpShifts,
        }}
        loading={!isPumpShiftsLoaded}
        columns={columns}
        data={pumpShifts}
        onChange={(pagination) => {
          setFilter({ ...filter, pageIndex: pagination.current || 1 })
          dispatch(
            setPumpShiftFilter({
              ...filter,
              pageIndex: pagination.current || 1,
            })
          )
        }}
        footer={() => (
          <Footer>
            <FooterElement>
              {`${translate('transaction.totalVolume', language)}: `}
              <NumberFormat
                value={totalVolumeOfPumpsShift}
                suffixKey="common.unitType"
              />
            </FooterElement>
            <FooterElement>
              {`${translate('transaction.totalAmount', language)}: `}
              <NumberFormat
                value={totalAmountOfPumpsShift}
                suffixKey="common.moneyType"
              />
            </FooterElement>
          </Footer>
        )}
      />
    </div>
  )
}
